import { useEffect, useState } from "react";
import { Container, Row, Col, Breadcrumb } from "react-bootstrap";
import UsersList from "../components/UsersList";
import ModalForm from "../components/ModalForm";
import AdminUserForm from "../components/AdminUserForm";
import SubmitButton from "../components/SubmitButton";
import Button from "../components/Button";
import styles from "./AdminUsers.module.scss";
import Loader from "../components/Loader";
import { alertModal, confirmModal } from "../utils/modalController";
import { Link } from "react-router-dom";
import { fetchAuth } from "../utils/fetchAuth";
import { formDataToJson } from "../utils/formDataToJson";
import SessionsList from "../components/SessionsList";

const apiUrl = process.env.REACT_APP_API_URL || '';

export default function AdminUsers() {
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showMenuModal, setShowMenuModal] = useState(false);
    const [showSessionsModal, setShowSessionsModal] = useState(false);
    const [showSessionModal, setShowSessionModal] = useState(false);
    const [selectedSession, setSelectedSession] = useState<Session | null>(null);
    const [modalTitle, setModalTitle] = useState("Novo usuário");
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [query, setQuery] = useState<string>("");

    const handleUserClick = (user: User) => {
        setShowMenuModal(true);
        setModalTitle("Editar usuário");
        setSelectedUser(user);
    };

    async function handleDeleteUser() {
        if (!await confirmModal("Tem certeza que deseja remover este usuário? Esta ação não pode ser desfeita.")) return;

        if (selectedUser) {
            setDeleting(true);
            fetchAuth(apiUrl + "/admin/users/" + selectedUser.id, {
                method: "DELETE",
            })
                .then(async (response) => {
                    const response_json = await response.json();
                    if (!response.ok) {
                        return Promise.reject(response_json.message || "Erro ao remover usuário");
                    }
                    return response_json;
                })
                .then((response_json) => {
                    setShowModal(false);
                    setShowMenuModal(false);
                    setUsers(users.filter(user => user.id !== selectedUser.id));
                    setSelectedUser(null);
                })
                .catch((error) => {
                    console.error("Erro ao remover usuário:", error);
                    alertModal(error.message);
                })
                .finally(() => {
                    setDeleting(false);
                });
        }
    }

    const handleAddUserClick = () => {
        setShowModal(true);
        setModalTitle("Novo usuário");
        setSelectedUser(null);
    };

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await fetchAuth(`${apiUrl}/admin/users?query=${query}`, {
                    method: "GET",
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Erro ao buscar usuários");
                }

                const response_json = await response.json();

                setUsers(response_json.data); // ou response_json, se não estiver aninhado
            } catch (error) {
                console.error("Erro:", error);
                alertModal("Erro ao buscar usuários: " + error);
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, [query]);


    return (
        <>
            <Container className={styles.container}>
                <h2 className={styles.title}>Administração</h2>
                <Row className={styles["row-breadcrumb"]}>
                    <Col>
                        <Breadcrumb className={styles.breadcrumb}>
                            <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/admin" }}>Administração</Breadcrumb.Item>
                            <Breadcrumb.Item active>Usuários</Breadcrumb.Item>
                        </Breadcrumb>
                    </Col>
                </Row>
                <Row className={styles["row-contas"]}>
                    <Col>
                        {loading ? (
                            <Loader />
                        ) : (
                            <UsersList
                                onUserClick={handleUserClick}
                                onAddUserClick={handleAddUserClick}
                                users={users}
                                onQueryChange={(query) => {
                                    setQuery(query);
                                }}
                            />
                        )}
                    </Col>
                </Row>
            </Container>
            <ModalForm
                title={selectedUser?.name || "Usuário"}
                show={showMenuModal}
                onHide={() => { setShowMenuModal(false) }}
            >
                <Row>
                    <Col className="text-center">
                        <Button
                            className={styles['buttons']}
                            onClick={() => { setShowModal(true) }}
                            variant="primary"
                        >
                            Editar
                        </Button>
                    </Col>
                    <Col className="text-center">
                        <Button
                            className={[styles['buttons'], selectedUser ? '' : 'd-none'].join(' ')}
                            onClick={() => {
                                setShowSessionsModal(true);
                            }}
                        >
                            Sessões ativas
                        </Button>
                    </Col>
                    <Col className="text-center">
                        <Button
                            className={[styles['buttons'], selectedUser ? '' : 'd-none'].join(' ')}
                            onClick={() => { handleDeleteUser() }}
                            variant="secondary"
                            disabled={!selectedUser || deleting}
                        >
                            {deleting ? "Removendo..." : "Remover"}
                        </Button>
                    </Col>
                </Row>
            </ModalForm>
            <ModalForm
                title={modalTitle}
                show={showModal}
                onHide={() => { setShowModal(false) }}
                onSubmit={(e) => {
                    e.preventDefault();
                    setSaving(true);
                    fetchAuth(apiUrl + `/admin/users${selectedUser ? `/${selectedUser.id}` : ''}`, {
                        method: selectedUser?.id ? "PUT" : "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(formDataToJson(new FormData(e.target as HTMLFormElement))),
                    })
                        .then(async (response) => {
                            const response_json = await response.json();
                            if (!response.ok) {
                                return Promise.reject(response_json.message || "Erro ao salvar usuário");
                            }
                            return response_json;
                        })
                        .then((response_json) => {
                            setShowModal(false);
                            if (selectedUser) {
                                setUsers(users.map(user => user.id === response_json.id ? {
                                    id: response_json.id,
                                    email: response_json.email,
                                    name: response_json.name,
                                    modified: response_json.modified,
                                } : user));
                            } else {
                                setUsers([...users, {
                                    id: response_json.id,
                                    email: response_json.email,
                                    name: response_json.name,
                                    modified: response_json.modified,
                                }]);
                            }
                        })
                        .catch((error) => {
                            console.error("Erro ao salvar usuário:", error);
                            alertModal("Erro ao salvar usuário: " + error);
                        })
                        .finally(() => {
                            setSaving(false);
                        });
                }}
                footerButtons={[
                    <Button
                        label="Cancelar"
                        className={styles['buttons']}
                        onClick={() => setShowModal(false)}
                        variant="secondary"
                        disabled={saving}
                    />,
                    <Button
                        className={[styles['buttons'], selectedUser ? '' : 'd-none'].join(' ')}
                        onClick={() => { handleDeleteUser() }}
                        variant="secondary"
                        disabled={saving || !selectedUser || deleting}
                    >
                        {deleting ? "Removendo..." : "Remover"}
                    </Button>,
                    <SubmitButton
                        className={styles['buttons']}
                        disabled={saving || deleting}
                    >
                        {saving ? "Salvando..." : "Salvar"}
                    </SubmitButton>,
                ]}
            >
                <AdminUserForm user={selectedUser ?? undefined} />
            </ModalForm>
            <ModalForm
                title={`Sessões de ${selectedUser?.name || "Usuário"}`}
                show={showSessionsModal}
                onHide={() => { setShowSessionsModal(false) }}
                footerButtons={[
                    <Button
                        label={deleting ? "Encerrando..." : "Encerrar sessões"}
                        className={styles['buttons']}
                        onClick={async () => {
                            if (!await confirmModal("Tem certeza que deseja encerrar todas as sessões deste usuário? Esta ação não pode ser desfeita.")) return;
                            if (!selectedUser) return;
                            setDeleting(true);
                            fetchAuth(apiUrl + `/admin/users/${selectedUser.id}/sessions`, {
                                method: "DELETE",
                            })
                                .then(() => {
                                    alertModal("Todas as sessões foram encerradas.");
                                    setShowSessionsModal(false);
                                })
                                .catch((error) => {
                                    console.error("Erro ao encerrar sessões:", error);
                                    alertModal("Erro ao encerrar sessões: " + error);
                                })
                                .finally(() => {
                                    setDeleting(false);
                                });
                        }}
                        variant="danger"
                    />,
                    <Button
                        label="Fechar"
                        className={styles['buttons']}
                        onClick={() => setShowSessionsModal(false)}
                        variant="secondary"
                    />
                ]}
            >
                <SessionsList user={selectedUser} handleSessionClick={() => {
                    setShowSessionModal(true);
                    setSelectedSession(selectedSession);
                }} />
            </ModalForm>
            <ModalForm
                title={`Sessão de ${selectedUser?.name || "Usuário"} : ${selectedSession ? selectedSession.id : ''}`}
                show={showSessionModal && selectedSession !== null}
                onHide={() => { setShowSessionModal(false); setSelectedSession(null); }}
                footerButtons={[
                    <Button
                        label="Encerrar sessão"
                        className={[styles['buttons'], (!selectedSession || selectedSession.revoked_at !== null || selectedSession.expires_at < new Date().toISOString()) ? 'd-none' : ''].join(' ')}
                        onClick={async () => {
                            fetchAuth(apiUrl + `/admin/sessions/${selectedSession?.id}`, {
                                method: "DELETE",
                            })
                                .then(() => {
                                    alertModal("Sessão encerrada.");
                                    setShowSessionModal(false);
                                    setSelectedSession(null);
                                })
                                .catch((error) => {
                                    console.error("Erro ao encerrar sessão:", error);
                                    alertModal("Erro ao encerrar sessão: " + error);
                                });
                        }}
                        variant="secondary"
                    />,
                    <Button
                        label="Fechar"
                        className={styles['buttons']}
                        onClick={() => setShowSessionModal(false)}
                        variant="secondary"
                    />
                ]}
            >
                {selectedSession ? (
                    <div>
                        <p><strong>ID:</strong> {selectedSession.id}</p>
                        <p><strong>IP:</strong> {selectedSession.browser.ip}</p>
                        <p><strong>Agente:</strong> {selectedSession.browser.user_agent}</p>
                        <p><strong>Criada em:</strong> {new Date(selectedSession.created_at).toLocaleString()}</p>
                        <p><strong>Última atividade:</strong> {new Date(selectedSession.logged_at).toLocaleString()}</p>
                        <p><strong>Expira em:</strong> {new Date(selectedSession.expires_at).toLocaleString()}</p>
                        {selectedSession.revoked_at && (
                            <p><strong>Revogada em:</strong> {new Date(selectedSession.revoked_at).toLocaleString()}</p>
                        )}
                    </div>
                ) : (
                    <p>Nenhuma sessão selecionada.</p>
                )}
            </ModalForm>
        </>
    );
}
